import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {loadModules} from 'esri-loader';

import {QueryObject, QueryObjectResults} from '../../interfaces/query';
import {ConfigService} from '../config/config.service';
import {BehaviorSubject} from 'rxjs';
import {GridService} from '../../services/grid/grid.service';
import esri = __esri;

/**
 * Formats and sends server queries to the appropriate server
 */
@Injectable({
  providedIn: 'root'
})
export class QueryService {
  public geometrySvcUrl: string;
  public mapWkid = 102100;  // default value, can overwrite in input queryTaskObj
  public projectedGeom: esri.Geometry;
  private findLocationObservable = new BehaviorSubject(null);
  public setFindLocationObservable(value) { this.findLocationObservable.next(value); }
  public getFindLocationObservable() { return this.findLocationObservable.asObservable(); }


  constructor(private http: HttpClient, private config: ConfigService, private grid: GridService) {}

  public async queryData(queryTaskObj: QueryObject) {
    try {
      const [Extent, SpatialReference] = await loadModules([
        'esri/geometry/Extent',
        'esri/geometry/SpatialReference'
      ]);

      // Create queryTaskResults object to hold the input parameters to the query task
      // plus the output created from the query task.
      const queryTaskResults: QueryObjectResults = {
        queryResults: {
          isQuerySuccessful: false // set initial status
        },
        queryTaskObj
      };

      if (queryTaskObj.layerType  && queryTaskObj.layerType === 'Raster Layer') {
        // Use identify for Raster Layers
        return await this.rasterIdentify(queryTaskObj);
      }

      // Get map wkid if needed
      if (queryTaskObj.mapWkid) {
        this.mapWkid = queryTaskObj.mapWkid;
      }

      if (!queryTaskObj.queryMethod || queryTaskObj.queryMethod.toUpperCase() !== 'POST') {
        queryTaskObj.queryMethod = 'GET';
      }

      if (queryTaskObj.geometry) {
        // Check to see if geometry needs to be projected
        // If geometry input, check that the spatial reference of the layer is the same as the map
        let layerWkid;
        if (queryTaskObj.layerElem && queryTaskObj.layerElem.serviceSpatialReference &&
          this.mapWkid &&
          (queryTaskObj.layerElem.serviceSpatialReference.wkid !== this.mapWkid)) {
          layerWkid = queryTaskObj.layerElem.serviceSpatialReference.wkid;
        } else {
          if (queryTaskObj.wkid && this.mapWkid && (queryTaskObj.wkid !== this.mapWkid)) {
            layerWkid = queryTaskObj.wkid;
          }
        }
        // ****Don't see a need to project data unless your querying data that is not in a GIS service like a local file
        //layerWkid = false;
        if (layerWkid && queryTaskObj.layerElem && queryTaskObj.layerElem.isLocalFile) {
          // geometry needs to be projected/transformed
          const extentArr = queryTaskObj.geometry.split(',');
          if (extentArr && extentArr.length === 4) {
            const layerExt = new Extent({xmin: Number(extentArr[0]), ymin : Number(extentArr[1]),
              xmax: Number(extentArr[2]), ymax: Number(extentArr[3]),
              spatialReference: new SpatialReference({ wkid: this.mapWkid})});
            if (layerExt) {
              // Project the geometry string to the spatial reference of the layer in order to query the layer
              const projectedGeom = await this.projectGeometry(layerExt, queryTaskObj.layerElem.serviceSpatialReference);
              if (projectedGeom) {
                queryTaskObj.geometry = this.getMapExtentString(projectedGeom);
              }
            }
          }
        }
      }  
      if (queryTaskObj.layerElem && queryTaskObj.layerElem.isLocalFile) {
        // call localfile queryData with geomMapExtentStr
        return await this.queryLocalData(queryTaskObj, queryTaskResults);
      } else {
        // Call service layer queryData
        return await this.queryServiceLayer(queryTaskObj, queryTaskResults);
      }

    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public async queryServiceLayer(queryTaskObj: any, queryTaskResults: any, buildQuery?: boolean) {
    try {
      const queryParams: FormData = new FormData();
      let jsonReq: string;
      
      // Build query task based on input parameters in the queryTaskObj
      if (queryTaskObj.layerTaskUrl !== undefined) {
        // Set default values if none specified
        if (queryTaskObj.returnGeometry === undefined) {
          // only returnGeometry when needed
          queryTaskObj.returnGeometry = false;
        }
        if (queryTaskObj.returnType === undefined) {
          queryTaskObj.returnType = 'FEATURES';
        }
        // if a returnType was specified (i.e COUNT, OBJECTIDS then set the appropriate properties)
        switch (queryTaskObj.returnType.toLowerCase()) {
          case 'count': // return count only
            queryTaskObj.countOnly = true;
            queryTaskObj.idsOnly = false;
            queryTaskObj.extentOnly = false;
            queryParams.append('countOnly', 'true');
            break;
          case 'objectids': // return objectids only
            queryTaskObj.idsOnly = true;
            queryTaskObj.countOnly = false;
            queryTaskObj.extentOnly = false;
            queryParams.append('idsOnly', 'true');
            break;
          case 'extent': // return extent of features 
            queryTaskObj.extentOnly = true;
            queryTaskObj.idsOnly = false;
            queryTaskObj.countOnly = false;
            queryParams.append('extentOnly', 'true');
            break;
          default:  // default returns features that meet criteria
            queryTaskObj.idsOnly = false;
            queryTaskObj.countOnly = false;
            queryTaskObj.extentOnly = false;
            break;
        }
        if ((! queryTaskObj.outFields) &&  (! queryTaskObj.countOnly)  &&
          (! queryTaskObj.idsOnly)  && (! queryTaskObj.returnDistinctValues) && (!queryTaskObj.extentOnly)) {
          // if outFields not specified, default to all fields (should only do this when necessary)
          queryParams.append('outFields', '*');
          queryTaskObj.outFields = ['*'];
        }

        // add datetime query to get around ESRI bug
        // [ NIM086349 - Alternate Query (Get) requests fail in Version 10.1 SP1 ]
        // http://forums.arcgis.com/threads/73932-query.task-intermitent-error-Unable-to-complete-operation
        const currDtTime: number = (new Date()).getTime();

        if (queryTaskObj.geometry === undefined &&
          (queryTaskObj.whereClause === undefined || queryTaskObj.whereClause.trim().length < 1)) {
          // whereClause is required unless this is a spatial query
          queryTaskObj.whereClause = currDtTime + '=' + currDtTime;
        }

        let queryStr = '';
        const objKeys = Object.keys(queryTaskObj);
        for (const key of objKeys) {
          if (queryTaskObj[key] !== undefined) {
            switch (key.toLowerCase()) {
              case 'whereclause':
                queryStr = queryStr + '&where=' + queryTaskObj[key];
                queryParams.append('where', queryTaskObj[key]);
                break;
              case 'outfields':
                queryStr = queryStr + '&outFields=' + queryTaskObj[key];
                queryParams.append('outFields', queryTaskObj[key]);
                break;
              case 'objectids':
                queryStr = queryStr + '&objectids=' + queryTaskObj[key];
                queryParams.append('objectids', queryTaskObj[key]);
                break;
              case 'relationshipclassids':
                queryStr = queryStr + '&relationshipId=' + queryTaskObj[key];
                queryParams.append('relationshipId', queryTaskObj[key]);
                break;
              case 'geometry':
                const geomStr = queryTaskObj[key];
                if (geomStr)
                {
                  geomStr.replace(/['"]+/g, '');
                }
                queryStr = queryStr + '&geometry=' + geomStr;
                queryParams.append('geometry', geomStr);
                if (this.mapWkid) {
                  queryStr = queryStr + '&inSR=' + this.mapWkid;
                  queryParams.append('inSR', this.mapWkid.toString());
                } 
                break;
              case 'geometrytype':
                queryStr = queryStr + '&geometryType=' + queryTaskObj[key];
                queryParams.append('geometryType', queryTaskObj[key]);
                break;
              case 'spatialrelationship':
                queryStr = queryStr + '&spatialRelationship=' + queryTaskObj[key];
                queryParams.append('spatialRelationship', queryTaskObj[key]);
                break;
              case 'geometryprecision':
                queryStr = queryStr + '&geometryPrecision=' + queryTaskObj[key];
                queryParams.append('geometryPrecision', queryTaskObj[key]);
                break;
              case 'orderbyfields':
                queryStr = queryStr + '&orderByFields=' + queryTaskObj[key];
                queryParams.append('orderByFields', queryTaskObj[key]);
                break;
              case 'returndistinctvalues':
                queryStr = queryStr + '&returnDistinctValues=' + queryTaskObj[key];
                queryParams.append('returnDistinctValues', queryTaskObj[key]);
                break;
              case 'returngeometry':
                queryStr = queryStr + '&returnGeometry=' + queryTaskObj[key];
                queryParams.append('returngeometry', queryTaskObj[key]);
                if (this.mapWkid) {
                  queryStr = queryStr + '&outSR=' + this.mapWkid;
                  queryParams.append('outSR', this.mapWkid.toString());
                }
                break;
              case 'startrow':
                queryStr = queryStr + '&resultOffset=' + Number(queryTaskObj[key]);
                queryParams.append('resultOffset', queryTaskObj[key]);
                break;
              case 'numreturnrows':
                queryStr = queryStr + '&resultRecordCount=' + Number(queryTaskObj[key]);
                queryParams.append('resultRecordCount', queryTaskObj[key]);
                break;
              case 'countonly':
                queryStr = queryStr + '&returnCountOnly=' + queryTaskObj[key];
                queryParams.append('returnCountOnly', queryTaskObj[key]);
                break;
              case 'extentonly':
                queryStr = queryStr + '&returnExtentOnly=' + queryTaskObj[key];
                queryParams.append('returnExtentOnly', queryTaskObj[key]);
                break;
              case 'idsonly':
                queryStr = queryStr + '&returnIdsOnly=' + queryTaskObj[key];
                queryParams.append('returnIdsOnly', queryTaskObj[key]);
                break;
              case 'token':
                queryStr = queryStr + '&token=' + queryTaskObj[key];
                queryParams.append('token', queryTaskObj[key]);
                break;
              default:
                break;
            }
          }
        }
        // console.log('queryStr: ' + queryStr);
        // Build query and parameters
        let queryType: string;
        if (queryTaskObj.relationshipClassIDS || queryTaskObj.relationshipClassIDS === 0) {
          queryType = 'queryRelatedRecords?';
        } else {
          queryType = 'query?';
        }
        jsonReq = queryTaskObj.layerTaskUrl + '/' + queryType;

        if (queryTaskObj.queryMethod === 'GET') {
          jsonReq = queryTaskObj.layerTaskUrl + '/' + queryType + queryStr + '&f=pjson';
        }
        queryTaskObj.jsonReq = jsonReq;

        queryParams.append('f', 'pjson');

        let results;
        if (buildQuery === undefined || buildQuery === false ) {
          if (queryTaskObj.queryMethod === 'POST') {
            const returnValue = await this.postRestApiData(jsonReq, queryParams);
            if (returnValue) {
              results = this.formatResponseData(queryTaskResults, returnValue);
            }
            return results;
          } else {
            const returnValue = await this.getRestApiData(jsonReq);
            if (returnValue) {
              results = this.formatResponseData(queryTaskResults, returnValue);
            }
            return results;
          }
        } else {
          queryTaskResults.queryResults.isQuerySuccessful = false;
          queryTaskResults.queryResults.errorMessage = 'Invalid input parameters: layer url';
          return queryTaskResults;
        }
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public async queryLocalData(queryTaskObj: any, queryTaskResults: any) {
    try {
      const [Extent, SpatialReference, GeometryEngine] = await loadModules([
        'esri/geometry/Extent',
        'esri/geometry/SpatialReference',
        'esri/geometry/geometryEngine'
      ]);
      let areFeatsInExtent = false;
      const layerElem = queryTaskObj.layerElem;
      const geomMapExtStr = queryTaskObj.geometry;
      let geomMapExt;
      // let featLay = layerElem.featureLayer;
      let returnValue: any = {};
      let results;
      // Create extent object if geometry passed in
      if (geomMapExtStr && layerElem.spatialReference && layerElem.spatialReference.wkid) {
        const extentArr = queryTaskObj.geometry.split(',');
        if (extentArr && extentArr.length === 4) {
          geomMapExt = new Extent({xmin: Number(extentArr[0]), ymin: Number(extentArr[1]),
            xmax: Number(extentArr[2]), ymax: Number(extentArr[3]),
            spatialReference: new SpatialReference({ wkid: layerElem.spatialReference.wkid})});
        }
      }
      if (layerElem.featureLayer && layerElem.featureLayer.source
        && layerElem.featureLayer.source.items) {
        const layFeats = layerElem.featureLayer.source.items;
        switch (queryTaskObj.returnType.toUpperCase()) {
          case 'FEATSEXIST': case 'COUNTONLY':
            let featCount = 0;
            if (geomMapExt) {
              for (const feature of layFeats) {
                // Check for any feature falling in current extent
                if (GeometryEngine.intersects(geomMapExt, feature.geometry)) {
                  areFeatsInExtent =  true;
                  if (queryTaskObj.returnType.toUpperCase() === 'FEATSEXIST') {
                    returnValue.featuresExist = areFeatsInExtent;
                    break;
                  }
                  featCount++;
                }
              }
              returnValue.featuresExist = areFeatsInExtent;
              returnValue.count = featCount;
            } else {
              returnValue.error = {message: 'Invalid geometry'};
            }
            results = this.formatResponseData(queryTaskResults, returnValue);
            break;
          default:  // default is to return features
            const layFeatsInExtent = [];
            if (geomMapExt) {
              for (const feature of layFeats) {
                // Check for any feature falling in current extent
                if (GeometryEngine.intersects(geomMapExt, feature.geometry)) {
                  layFeatsInExtent.push(feature);
                }
              }
              returnValue = layFeatsInExtent;
            } else {
              // return all feats
              returnValue = layFeats;
            }
            const outFeatureSet = this.formatFeatureSet(layerElem.fileType, returnValue);
            results = this.formatResponseData(queryTaskResults, outFeatureSet);
            break;
        }
        return results;
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public formatFeatureSet(fileType: string, featureSetArr: any) {
    const outFeatureSet: any = {};
    let fields;
    let geometryType;
    let displayFieldName;
    let objectIdFieldName;
    let spatialReference;
    if (featureSetArr) {
      outFeatureSet.featureCount = featureSetArr.length;
      const feats = [];
      for (const feature of featureSetArr) {
        feats.push({attributes: feature.attributes,
          geometry: feature.geometry });
        if (feature.layer) {
          fields = feature.layer.fields;
          geometryType = feature.layer.geometryType;
          if (feature.layer.objectIdField) {
            objectIdFieldName = feature.layer.objectIdField;
          }
          if (feature.layer.spatialReference) {
            spatialReference = feature.layer.spatialReference;
          }
          if (feature.layer.displayField) {
            displayFieldName = feature.layer.displayField;
          } else {
            if (feature.layer.objectIdField) {
              displayFieldName = feature.layer.objectIdField;
            } else {
              displayFieldName = fields[0].name;
            }
          }
        }
      }
      outFeatureSet.features = feats;
      let newFields = [];
      if (fields) {
        for (const field of fields) {
          if (field.name && (field.name !==  objectIdFieldName) &&
            (field.name.toLowerCase() !== 'shape')) {
            newFields.push(field);
          }
        }
        if (newFields && (newFields.length < 1) && fields && (fields.length > 0)) {
          // Only fields in this layer, may be objectid and shape fields so add the original
          // fields to newFields so some attribute will show for this layer.
          newFields = fields;
        }
      }
      outFeatureSet.fields = newFields;
      outFeatureSet.geometryType = geometryType;
      outFeatureSet.displayFieldName = displayFieldName;
      if (objectIdFieldName) {
        outFeatureSet.objectIdFieldName = objectIdFieldName;
      }
      if (spatialReference) {
        outFeatureSet.spatialReference = spatialReference;
      }
    }
    return outFeatureSet;

  }

  public formatResponseData(queryTaskResults: QueryObjectResults, returnValue: any) {
    const queryTaskObj = queryTaskResults.queryTaskObj;

    if (returnValue !== undefined) {
      if (returnValue.error === undefined) {
        queryTaskResults.queryResults.isQuerySuccessful = true;
        if (queryTaskObj.returnType === undefined) {
          queryTaskObj.returnType = 'FEATURES';
        }
        if (queryTaskObj.returnType) {
          switch (queryTaskObj.returnType.toUpperCase()) {
            case 'COUNT':
              queryTaskResults.queryResults.featureCount = returnValue.count;
              break;
            case 'FEATSEXIST':
              queryTaskResults.queryResults.featuresExist = returnValue.featuresExist;
              break;
            case 'OBJECTIDS':
              if (returnValue.objectIds) {
                queryTaskResults.queryResults.objectIdArr = returnValue.objectIds;
                queryTaskResults.queryResults.objectIdFieldName = returnValue.objectIdFieldName;
                queryTaskResults.queryResults.objectidCount = returnValue.objectIds.length;
              } else {
                queryTaskResults.queryResults.objectIdArr = [];
                queryTaskResults.queryResults.objectIdFieldName = '';
                queryTaskResults.queryResults.objectidCount = 0;
              }
              break;
            default:
              // Default return type is a featureSet
              queryTaskResults.queryResults.featureSet = returnValue;
              if (queryTaskResults.queryResults.featureSet
                && queryTaskResults.queryResults.featureSet.features) {
                queryTaskResults.queryResults.exceededTransferLimit
                  = queryTaskResults.queryResults.featureSet.exceededTransferLimit;

                if (queryTaskResults.queryResults.featureSet.features) {
                  queryTaskResults.queryResults.featureCount
                    = queryTaskResults.queryResults.featureSet.features.length;
                } else {
                  // No features for this query
                  console.log('No features for this query. '
                    + queryTaskObj.layerElem.serviceLayerID.toUpperCase());
                  queryTaskResults.queryResults.featureCount = 0;
                }
              }
              break;
          }
        }
      } else {
        // Error
        queryTaskResults.queryResults.isQuerySuccessful = false;
        queryTaskResults.queryResults.errorMessage = returnValue.error.message;
        queryTaskResults.queryResults.error = returnValue.error;
      }
    }
    return queryTaskResults;
  }

  public async getRestApiData(jsonReq: string) {
    try {
      return await this.http.get(jsonReq).toPromise();
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public async postRestApiData(svcUrl: string, args: any) {
    try {
      const options = {};
      return await this.http.post(svcUrl, args, options).toPromise();
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  // Projection functions
  public async projectGeometry(fromGeomObj: esri.Geometry, toSpatialRef: esri.SpatialReference) {
    try {
      const [SpatialReference, WebMercatorUtils] = await loadModules([
        'esri/geometry/SpatialReference',
        'esri/geometry/support/webMercatorUtils'
      ]);
      if (fromGeomObj.spatialReference  && toSpatialRef &&
        fromGeomObj.spatialReference.wkid !== toSpatialRef.wkid) {
        const toSpatialRefNew = new SpatialReference({wkid: toSpatialRef.wkid});
        const canProj: boolean = WebMercatorUtils.canProject(fromGeomObj.spatialReference, toSpatialRefNew);
        if (canProj) {
          this.projectedGeom = WebMercatorUtils.project(fromGeomObj, toSpatialRef);
          return fromGeomObj;
        } else {
          return await this.projectGeometrySvc(fromGeomObj, toSpatialRefNew);
        }
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public async projectGeometrySvc(fromGeomObj: esri.Geometry, toSpatialRef: esri.SpatialReference) {
    try {
      const [SpatialReference, GeometryService, ProjectParameters] = await loadModules([
        'esri/geometry/SpatialReference',
        'esri/tasks/GeometryService',
        'esri/tasks/support/ProjectParameters'
      ]);
      // Project data using geometry service
      if ((!this.geometrySvcUrl) && this.config
        && this.config.geometryServer) {
        this.geometrySvcUrl = this.config.geometryServer;
      }
      if (this.geometrySvcUrl) {
        const geomSvc = new GeometryService({url: this.geometrySvcUrl});
        const params = new ProjectParameters();
        const toSpatialRefNew = new SpatialReference({wkid: toSpatialRef.wkid});
        params.geometries = [fromGeomObj];
        params.outSR = toSpatialRefNew;  // Not sure if this one is needed
        params.outSpatialReference = toSpatialRefNew;
        // params.transformation = transformation;
        const projectedGeoms = await geomSvc.project(params);
        // The promise resolves to the value stored in projectedGeoms
        return projectedGeoms[0]; // return 1st geometry object since only 1 is passed in
      } else {
        console.log('No geometry service');
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public getMapExtentString(geomObj) {
    return geomObj.xmin.toString() + ',' + geomObj.ymin.toString() + ',' + geomObj.xmax.toString() + ','
      + geomObj.ymax.toString();
  }

   /**
   * Format the geometry objects
   */
  public getESRIGeometryObj(featGeom){
    let geometryObj;
    switch(featGeom.type) {
      case 'point':
        geometryObj = {
          geometry: 'x: ' + featGeom.x + ', y: ' + featGeom.y,
          geometryType: 'esriGeometryPoint'
        }
        break;
      case 'polyline':
        geometryObj = {
          geometry: '{paths:' + JSON.stringify(featGeom.paths) + '}',
          geometryType: 'esriGeometryPolyline'
        }
        break;
      case 'polygon':
        geometryObj = {
          geometry : '{rings:' + JSON.stringify(featGeom.rings) + '}',
          geometryType: 'esriGeometryPolygon'
        }
        break;
      default:
        geometryObj = {
          geometry : '{rings:' + JSON.stringify(featGeom.rings) + '}',
          geometryType: 'esriGeometryEnvelope'
        }
        break;
    }
    return geometryObj;
  }

  /**
   * Sends the identify request for a raster layer
   */
  public async rasterIdentify(queryObj: any) {
    try {
      let url = queryObj.layerTaskUrl + '/identify?geometry=' +  queryObj.geometry
        + '&geometryType=esriGeometryPoint&layers=all:' + queryObj.layerID
        + '&layerDefs=&time=&layerTimeOptions=&tolerance=0&mapExtent='
        + queryObj.extent.xmin + ',' + queryObj.extent.ymin + ',' + queryObj.extent.xmax + ','
        + queryObj.extent.ymax +  '&imageDisplay=' + queryObj.viewSize[0] + ',' +  queryObj.viewSize[1]
        + ',96' + '&returnGeometry=false&maxAllowableOffset=&geometryPrecision='
        + '&dynamicLayers=&returnZ=false&returnM=false&gdbVersion==&f=pjson';
      if (queryObj.token) {
        url = url + '&token=' + queryObj.token;
      }
      let data: any = await this.http.get(url).toPromise();
      data = data.results;
      if (data && data[0]) {
        if (data[0].attributes) {
          if (data[0].attributes.COUNT) {
            delete data[0].attributes.COUNT;
          }
          if (data[0].attributes.count) {
            delete data[0].attributes.count;
          }
          if (data[0].attributes['Class value']) {
            delete data[0].attributes['Class value'];
          }
          if (data[0].attributes['Class Value']) {
            delete data[0].attributes['Class Value'];
          }
          if (data[0].attributes['Pixel Value']) {
            data[0].attributes.Value =
              data[0].attributes['Pixel Value'];
            delete data[0].attributes['Pixel Value'];
          } else if (data[0].attributes['Pixel value']) {
            data[0].attributes.Value =
              data[0].attributes['Pixel value'];
            delete data[0].attributes['Pixel value'];
          }
        }
        return data;
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  /**
   * Notifys Township/Range/Section whereclause to observers
   */
  public async notifyTRSObservers(whereClause) {
    // Send find location whereClause to components that subscribe to them
    this.setFindLocationObservable(whereClause);
    // Turn on traxs report widget to display traxs info for this t/r/s
    await this.grid.widgetOff('traxsreport');
    await this.grid.widgetOn('traxsreport')
  }

}
